import { useCallback, useEffect, useRef, useState } from 'react';
import { toolsApi } from '../../services/toolsApi';
import { errorText } from '../../services/platformApi';

const POLL_MS = 3000;
const MAX_WAIT_MS = 30 * 60 * 1000;      // обработка не должна идти дольше (реальное время)
const MAX_OUTAGE_MS = 10 * 60 * 1000;    // сколько терпим недоступность сервера подряд

const INITIAL = {
  phase: 'idle',            // idle | uploading | processing | done | error
  uploadPct: 0, taskId: null, status: null, progress: 0, message: '', queuePosition: null,
  elapsed: 0, reconnecting: false, results: null, error: '',
};

/**
 * Жизненный цикл задачи инструмента: загрузка -> ожидание -> результат.
 * Опрос одним циклом по реальному времени, переживает перезапуск сервера обработки (статус unreachable / 503).
 */
const ONE_HOUR = 60 * 60 * 1000;   // столько результаты хранятся на сервере обработки

const loadSaved = (key) => {
  try {
    const raw = key && window.localStorage.getItem(key);
    const saved = raw && JSON.parse(raw);
    if (saved && saved.taskId && Date.now() - saved.at < ONE_HOUR) return saved;
    if (raw) window.localStorage.removeItem(key);
  } catch (e) { /* хранилище недоступно */ }
  return null;
};
const saveTask = (key, taskId) => {
  try { if (key) window.localStorage.setItem(key, JSON.stringify({ taskId, at: Date.now() })); } catch (e) { /* не критично */ }
};
const forgetTask = (key) => {
  try { if (key) window.localStorage.removeItem(key); } catch (e) { /* не критично */ }
};

/**
 * storageKey — ключ для запоминания текущей задачи: после обновления страницы или возврата позже
 * результат (он хранится на сервере около часа) подхватывается автоматически.
 */
export default function useToolTask(storageKey, api = toolsApi) {
  const [state, setState] = useState(INITIAL);
  const stopRef = useRef(false);
  const timerRef = useRef(null);
  const taskRef = useRef(null);

  const patch = useCallback((p) => setState((s) => ({ ...s, ...p })), []);

  const stop = useCallback(() => {
    stopRef.current = true;
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const poll = useCallback((taskId) => {
    stopRef.current = false;
    const startedAt = Date.now();
    let failingSince = null;

    const fail = (error) => { stop(); forgetTask(storageKey); patch({ phase: 'error', error, reconnecting: false }); };

    const tick = async () => {
      if (stopRef.current) return;
      patch({ elapsed: (Date.now() - startedAt) / 1000 });
      if (Date.now() - startedAt > MAX_WAIT_MS) { fail('timeout'); return; }
      try {
        const data = await api.status(taskId);
        if (stopRef.current) return;
        if (data && data.status === 'unreachable') throw new Error('unreachable');
        failingSince = null;
        patch({
          reconnecting: false, status: data.status, progress: typeof data.progress === 'number' ? data.progress : 0,
          message: data.message || '', queuePosition: data.queue_position ?? null,
        });
        if (data.status === 'completed') {
          const results = await api.results(taskId);
          if (stopRef.current) return;
          stop();
          patch({ phase: 'done', results, progress: 100 });
          return;
        }
        if (data.status === 'failed' || data.status === 'cancelled') { fail(data.message || ''); return; }
      } catch (err) {
        if (stopRef.current) return;
        if (err?.response?.status === 404) { fail(errorText(err)); return; }
        failingSince = failingSince || Date.now();
        patch({ reconnecting: true });
        if (Date.now() - failingSince > MAX_OUTAGE_MS) { fail('outage'); return; }
      }
      timerRef.current = setTimeout(tick, POLL_MS);
    };
    tick();
  }, [patch, stop, storageKey]);

  const begin = useCallback(async (request) => {
    stop();
    setState({ ...INITIAL, phase: 'uploading' });
    try {
      const response = await request((pct) => patch({ uploadPct: pct }));
      taskRef.current = response.task_id;
      saveTask(storageKey, response.task_id);
      patch({ phase: 'processing', taskId: response.task_id, status: response.status, uploadPct: 100 });
      poll(response.task_id);
    } catch (err) {
      patch({ phase: 'error', error: errorText(err) });
    }
  }, [patch, poll, stop, storageKey]);

  const start = useCallback((kind, formData) => begin((onProgress) => api.start(kind, formData, onProgress)), [begin, api]);
  const rerun = useCallback((params) => begin(() => api.rerun(taskRef.current, params)), [begin, api]);

  const cancel = useCallback(async () => {
    const id = taskRef.current;
    stop();
    try { if (id) await api.cancel(id); } catch (e) { /* задача могла уже завершиться */ }
    forgetTask(storageKey);
    setState(INITIAL);
  }, [stop, storageKey, api]);

  const reset = useCallback(() => { stop(); forgetTask(storageKey); taskRef.current = null; setState(INITIAL); }, [stop, storageKey]);

  // Возврат на страницу: если есть недавняя задача, продолжаем следить за ней
  useEffect(() => {
    const saved = loadSaved(storageKey);
    if (saved) {
      taskRef.current = saved.taskId;
      setState({ ...INITIAL, phase: 'processing', taskId: saved.taskId, message: '' });
      poll(saved.taskId);
    }
    return () => stop();
  }, [storageKey]);

  return { ...state, start, rerun, cancel, reset };
}
